package top.wcpe.allincore.ui.runtime.ir.call

import top.wcpe.allincore.dsl.ir.expr.ExprFormatter
import top.wcpe.allincore.dsl.ir.expr.ExprNode
import top.wcpe.allincore.ui.runtime.animation.Easing
import top.wcpe.allincore.ui.runtime.element.InfiniteScroll
import top.wcpe.allincore.ui.runtime.element.PageView
import top.wcpe.allincore.ui.runtime.element.PullToRefresh
import top.wcpe.allincore.ui.runtime.element.ScrollableContainer
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.element.VirtualTree
import top.wcpe.allincore.ui.runtime.element.Zoomable
import top.wcpe.allincore.ui.runtime.host.TimerAwaitable
import top.wcpe.allincore.ui.runtime.host.TimerHost
import top.wcpe.allincore.ui.runtime.host.UiTask
import top.wcpe.allincore.ui.runtime.internal.text.formatStringCompat
import top.wcpe.allincore.ui.runtime.ir.DslRuntimeError
import top.wcpe.allincore.ui.runtime.ir.DslValueUtils
import top.wcpe.allincore.ui.runtime.ir.api.DateApi
import top.wcpe.allincore.ui.runtime.ir.api.DebugApi
import top.wcpe.allincore.ui.runtime.ir.api.DisplayApi
import top.wcpe.allincore.ui.runtime.ir.api.InputApi
import top.wcpe.allincore.ui.runtime.ir.api.ItemView
import top.wcpe.allincore.ui.runtime.ir.api.LocalApi
import top.wcpe.allincore.ui.runtime.ir.api.LocalPlayerView
import top.wcpe.allincore.ui.runtime.ir.api.OptionsApi
import top.wcpe.allincore.ui.runtime.ir.api.PersistApi
import top.wcpe.allincore.ui.runtime.ir.api.SessionApi
import top.wcpe.allincore.ui.runtime.ir.api.SlotApi
import top.wcpe.allincore.ui.runtime.ir.api.TimeApi
import top.wcpe.allincore.ui.runtime.ir.api.TimerApi
import top.wcpe.allincore.ui.runtime.ir.api.UiCommandApi
import top.wcpe.allincore.ui.runtime.ir.api.VanillaApi
import top.wcpe.allincore.ui.runtime.ir.api.WorldApi
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext
import top.wcpe.allincore.ui.runtime.ir.eval.EventMap
import top.wcpe.allincore.ui.runtime.ir.eval.ExprEvaluator
import top.wcpe.allincore.ui.runtime.ir.subscribe.SubscriptionValue
import top.wcpe.allincore.ui.runtime.render.RendererRegistry
import top.wcpe.allincore.ui.runtime.theme.ThemeManager
import top.wcpe.allincore.ui.runtime.theme.ThemeTransition
import kotlin.math.floor

/**
 * 成员方法调用分派（拆分自 [CallDispatcher]）。
 *
 * 集中处理 DSL 中所有 [ExprNode.MemberCall] / SafeMemberCall 形态：根据 target
 * 运行时类型选择对应内建方法分派——LocalApi / WorldApi / OptionsApi / InputApi /
 * VanillaApi / SlotApi / Timer* / Session / Persist / Subscription / 各类 typed
 * payload helper（Map / List / String / Number）等。
 *
 * 依赖 DAG：实参强转走 [CallArgs.toDouble]；timer 回调经 [CallableInvoker.invokeCallable]
 * 调用。Time / Date / Debug 仍委托既有 [TimeDateApiInvoker] / [DebugApiInvoker]。
 */
@Suppress("TooManyFunctions", "LargeClass", "MagicNumber")
internal object MemberCallDispatcher {

    // SPLICE_MEMBER

    /**
     * 分派 MemberCall：根据 target 类型选择对应的内建方法。
     *
     * 当前支持 [LocalApi] 目标的 `openScreen(String)` / `back()`，
     * 以及 [Map] 目标的 typed payload helper（number / string / bool / map / list）。
     *
     * 抛错时会把原始 MemberCall 表达式（含 target / method / args 可读形式）拼到错误消息里，
     * 帮助用户从 conditional 容器位置 + 表达式形态定位 DSL 源码出错点（IR wire 暂未携带行列号）。
     */
    fun evalMemberCall(call: ExprNode.MemberCall, ctx: EvalContext): Any? {
        val target: Any?
        try {
            target = ExprEvaluator.eval(call.target, ctx)
        } catch (e: DslRuntimeError) {
            // target 求值失败：附加调用链上下文后重抛（用 ExprFormatter 输出 DSL 源码风格）
            throw DslRuntimeError(
                "MemberCall target 求值失败：${ExprFormatter.format(call)} —— ${e.message}",
                e,
            )
        }
        try {
            return invokeMemberCall(target, call.method, call.args, ctx)
        } catch (e: DslRuntimeError) {
            // 调用失败：附加 DSL 源码风格表达式 + target 实际类型 + 原异常 cause
            throw DslRuntimeError(
                "MemberCall 调用失败：${ExprFormatter.format(call)}" +
                    "（target 实际类型=${DslValueUtils.classOf(target)}）—— ${e.message}",
                e,
            )
        }
    }

    /** 抽出真正的方法分派，供 MemberCall / SafeMemberCall 复用。 */
    @Suppress("CyclomaticComplexMethod", "LongMethod", "ReturnCount", "NestedBlockDepth", "CognitiveComplexMethod")
    fun invokeMemberCall(target: Any?, method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? {
        if (target is LocalApi) {
            return invokeLocalApi(target, method, argNodes, ctx)
        }
        if (target is WorldApi) {
            return invokeWorldApi(target, method, argNodes, ctx)
        }
        // Phase 2：client.options.* 方法分派
        if (target is OptionsApi) {
            return invokeOptionsApi(target, method, argNodes, ctx)
        }
        // Phase 2：client.input.* 方法分派
        if (target is InputApi) {
            return invokeInputApi(target, method, argNodes, ctx)
        }
        if (target is VanillaApi) {
            return invokeVanillaApi(target, method, argNodes, ctx)
        }
        if (target is SlotApi) {
            return invokeSlotApi(target, method, argNodes, ctx)
        }
        if (target is DisplayApi) {
            return invokeDisplayApi(target, method, argNodes, ctx)
        }
        if (target is UiCommandApi.ElementHandle) {
            return invokeElementHandle(target, method, argNodes, ctx)
        }
        if (target is TimeApi) {
            return TimeDateApiInvoker.invokeTime(method, argNodes, ctx)
        }
        if (target is DateApi) {
            return TimeDateApiInvoker.invokeDate(method, argNodes, ctx)
        }
        if (target is TimerApi) {
            return invokeTimerApi(method, argNodes, ctx)
        }
        if (target is DebugApi) {
            return DebugApiInvoker.invoke(method, argNodes, ctx)
        }
        if (target is TimerHandleValue) {
            return invokeTimerHandle(target, method, argNodes)
        }
        if (target is SessionApi) {
            return invokeSessionApi(target, method, argNodes)
        }
        if (target is PersistApi) {
            return invokePersistApi(target, method, argNodes)
        }
        if (target is ItemView) {
            return invokeItemView(target, method, argNodes, ctx)
        }
        if (target is SubscriptionValue) {
            return invokeSubscriptionValue(target, method, argNodes)
        }
        if (target is String) {
            return invokeStringHelper(target, method, argNodes, ctx)
        }
        if (target is EventMap) {
            // EventMap 的 consume / isConsumed 优先派发到底层事件；其它方法 fall-through
            // 到普通 Map helper，保持 string/number/bool/map/list/array 等能力不变。
            val em: EventMap = target
            when (method) {
                "consume" -> {
                    requireArgCount("event.consume()", argNodes, 0)
                    em.consume()
                    return null
                }
                "isConsumed" -> {
                    requireArgCount("event.isConsumed()", argNodes, 0)
                    return em.isConsumed()
                }
                else -> {
                    // fall-through 到 Map helper
                }
            }
        }
        // LocalPlayerView 在通用 Map 分支前优先识别，让 local.player.sendMessage / chat 走专用通道；
        // 其它方法（如 typed payload helper）落到下面的 Map helper 分支。
        if (target is LocalPlayerView) {
            val playerView: LocalPlayerView = target
            when (method) {
                "sendMessage" -> {
                    requireArgCount("local.player.sendMessage()", argNodes, 1)
                    val text = stringArg("local.player.sendMessage()", argNodes, 0, ctx)
                    playerView.sendMessage(text)
                    return null
                }
                "chat" -> {
                    requireArgCount("local.player.chat()", argNodes, 1)
                    val text = stringArg("local.player.chat()", argNodes, 0, ctx)
                    playerView.chat(text)
                    return null
                }
                else -> {
                    // 其它方法 fall-through 到 Map helper（typed payload helper 等）
                }
            }
        }
        if (target is Map<*, *>) {
            return invokeMapHelper(target, method, argNodes, ctx)
        }
        if (target is List<*>) {
            return invokeListHelper(target, method, argNodes, ctx)
        }
        if (target is Number) {
            return invokeNumberHelper(target, method, argNodes, ctx)
        }
        throw DslRuntimeError("不支持的 MemberCall 目标类型: ${DslValueUtils.classOf(target)}")
    }

    /** SessionApi 上的方法分派：clear() 清空当前进程的所有 session 字段（测试用）。 */
    private fun invokeSessionApi(api: SessionApi, method: String, argNodes: List<ExprNode>): Nothing? {
        if ("clear" == method) {
            requireArgCount("session.clear()", argNodes, 0)
            api.clear()
            return null
        }
        throw DslRuntimeError("SessionApi 不支持的方法: $method")
    }

    /** PersistApi 上的方法分派：save() / reload() 主动落盘 / 重读。 */
    private fun invokePersistApi(api: PersistApi, method: String, argNodes: List<ExprNode>): Nothing? {
        when (method) {
            "save" -> {
                requireArgCount("persist.save()", argNodes, 0)
                api.save()
                return null
            }
            "reload" -> {
                requireArgCount("persist.reload()", argNodes, 0)
                api.reload()
                return null
            }
            else -> throw DslRuntimeError("PersistApi 不支持的方法: $method")
        }
    }

    /**
     * TimerApi 方法分派：timer.timeout / interval / delayAsync / compute / cancelAll。
     *
     * 调用前提：EvalContext 持有 TimerHost；未注入时（同步测试模式）抛 DslRuntimeError。
     *
     * 对于 timeout / interval：lambda 是位置实参的最后一个（DSL 解析时 trailing lambda 已并入 args 列表）。
     * 对于 delayAsync：仅一个 ms 参数，返回 PendingAwaitable，配合 await 使用。
     * 对于 compute：lambda 是位置实参，返回 PendingAwaitable，await 后得 lambda 末位求值。
     */
    @Suppress("CyclomaticComplexMethod", "ThrowsCount", "LongMethod", "ReturnCount")
    private fun invokeTimerApi(method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? {
        val host: TimerHost = ctx.timerHost
            ?: throw DslRuntimeError("timer.* 仅在异步上下文（DslExecutor 已注入）可用")
        when (method) {
            "timeout" -> {
                if (argNodes.size != 2) {
                    throw DslRuntimeError(
                        "timer.timeout(delayMs, lambda) 需要 2 个参数（含 trailing lambda），实际 ${argNodes.size}",
                    )
                }
                val delay = longArg("timer.timeout()", argNodes, 0, ctx)
                val cb = ExprEvaluator.eval(argNodes[1], ctx)
                requireCallable("timer.timeout() 第 2 个参数", cb)
                return TimerHandleValue(
                    host.timeout(delay) {
                        CallableInvoker.invokeCallable(cb, listOf<Any?>(null), ctx, "timer.timeout")
                    },
                )
            }
            "interval" -> {
                if (argNodes.size != 2 && argNodes.size != 3) {
                    throw DslRuntimeError(
                        "timer.interval(periodMs, lambda) 或 timer.interval(initialDelayMs, periodMs, lambda) " +
                            "参数个数不匹配，实际 ${argNodes.size}",
                    )
                }
                val initial: Long
                val period: Long
                val cb: Any?
                if (argNodes.size == 2) {
                    period = longArg("timer.interval()", argNodes, 0, ctx)
                    initial = period
                    cb = ExprEvaluator.eval(argNodes[1], ctx)
                } else {
                    initial = longArg("timer.interval()", argNodes, 0, ctx)
                    period = longArg("timer.interval()", argNodes, 1, ctx)
                    cb = ExprEvaluator.eval(argNodes[2], ctx)
                }
                requireCallable("timer.interval() 最后一个参数", cb)
                return TimerHandleValue(
                    host.interval(initial, period) {
                        CallableInvoker.invokeCallable(cb, listOf<Any?>(null), ctx, "timer.interval")
                    },
                )
            }
            "delayAsync" -> {
                requireArgCount("timer.delayAsync()", argNodes, 1)
                val ms = longArg("timer.delayAsync()", argNodes, 0, ctx)
                return TimerAwaitable.ofDelay(host, ms)
            }
            "compute" -> {
                requireArgCount("timer.compute()", argNodes, 1)
                val cb = ExprEvaluator.eval(argNodes[0], ctx)
                requireCallable("timer.compute() 第 1 个参数", cb)
                return TimerAwaitable.ofCompute(host) {
                    CallableInvoker.invokeCallable(cb, listOf<Any?>(null), ctx, "timer.compute")
                }
            }
            "cancelAll" -> {
                requireArgCount("timer.cancelAll()", argNodes, 0)
                host.dispose()
                return null
            }
            else -> throw DslRuntimeError("TimerApi 不支持的方法: $method")
        }
    }

    /** TimerHandleValue 上的方法分派：cancel() / isCancelled()。 */
    private fun invokeTimerHandle(handle: TimerHandleValue, method: String, argNodes: List<ExprNode>): Any? {
        when (method) {
            "cancel" -> {
                requireArgCount("TimerHandle.cancel()", argNodes, 0)
                handle.cancel()
                return null
            }
            "isCancelled" -> {
                requireArgCount("TimerHandle.isCancelled()", argNodes, 0)
                return handle.isCancelled()
            }
            else -> throw DslRuntimeError("TimerHandle 不支持的方法: $method")
        }
    }

    /** 轻量"可调用对象"校验：与 net.subscribe handler 同款类型限定。 */
    @Suppress("ComplexCondition")
    private fun requireCallable(hint: String, value: Any?) {
        if (value !is LambdaValue &&
            value !is BoundFunRefValue &&
            value !is FunRefValue &&
            value !is LocalFunValue
        ) {
            throw DslRuntimeError(
                "$hint 必须为可调用对象（lambda / fun ref），实际类型: ${DslValueUtils.classOf(value)}",
            )
        }
    }

    /** longArg：把第 N 个表达式求值为 long；非数值抛 DslRuntimeError。 */
    private fun longArg(hint: String, argNodes: List<ExprNode>, index: Int, ctx: EvalContext): Long {
        val v = ExprEvaluator.eval(argNodes[index], ctx)
        if (v !is Number) {
            throw DslRuntimeError(
                "$hint 第 ${index + 1} 个参数必须为 Number，实际类型: ${DslValueUtils.classOf(v)}",
            )
        }
        return v.toLong()
    }

    /**
     * SubscriptionValue 上的方法分派（B8 扩展）。
     *
     * 支持：
     * - `cancel()` — 取消底层订阅，返回 null（幂等）
     * - `isCancelled()` — 返回当前取消状态（Boolean）
     *
     * 由 DSL 中 `var s = net.subscribe(...); ...; s.cancel()` 等写法触发。
     * 配合 IrSession.dispose 的自动 cancel，DSL 用户既能在 session 内主动取消订阅，
     * 也能依赖屏幕销毁时的自动清理。
     */
    private fun invokeSubscriptionValue(target: SubscriptionValue, method: String, argNodes: List<ExprNode>): Any? {
        when (method) {
            "cancel" -> {
                requireArgCount("SubscriptionValue.cancel()", argNodes, 0)
                target.cancel()
                return null
            }
            "isCancelled" -> {
                requireArgCount("SubscriptionValue.isCancelled()", argNodes, 0)
                return target.isCancelled()
            }
            else -> throw DslRuntimeError("SubscriptionValue 不支持的方法: $method")
        }
    }

    /**
     * M6-f §8.4：Number 上的 helper。当前仅支持 `toFixed(digits: Number): String`。
     */
    @Suppress("ThrowsCount")
    private fun invokeNumberHelper(target: Number, method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? {
        if ("toFixed" != method) {
            throw DslRuntimeError("Number 不支持的方法: $method")
        }
        if (argNodes.size != 1) {
            throw DslRuntimeError("Number.toFixed() 需要 1 个参数，实际 ${argNodes.size}")
        }
        val v = ExprEvaluator.eval(argNodes[0], ctx)
        if (v !is Number) {
            throw DslRuntimeError("Number.toFixed() 参数必须为 Number，实际类型: ${DslValueUtils.classOf(v)}")
        }
        val digits = v.toInt()
        if (digits < 0) {
            throw DslRuntimeError("Number.toFixed() 参数必须非负，实际: $digits")
        }
        return formatStringCompat("%.${digits}f", arrayOf(target.toDouble()))
    }

    /** String 上的 helper：常用文本操作与 Minecraft 颜色前缀处理。 */
    @Suppress("CyclomaticComplexMethod", "LongMethod", "ReturnCount")
    private fun invokeStringHelper(target: String, method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? {
        when (method) {
            "length" -> {
                requireArgCount("String.length()", argNodes, 0)
                return target.length.toDouble()
            }
            "isEmpty" -> {
                requireArgCount("String.isEmpty()", argNodes, 0)
                return target.isEmpty()
            }
            "isBlank" -> {
                requireArgCount("String.isBlank()", argNodes, 0)
                return target.trim().isEmpty()
            }
            "contains" -> {
                requireArgCount("String.contains()", argNodes, 1)
                return target.contains(stringArg("String.contains()", argNodes, 0, ctx))
            }
            "startsWith" -> {
                requireArgCount("String.startsWith()", argNodes, 1)
                return target.startsWith(stringArg("String.startsWith()", argNodes, 0, ctx))
            }
            "endsWith" -> {
                requireArgCount("String.endsWith()", argNodes, 1)
                return target.endsWith(stringArg("String.endsWith()", argNodes, 0, ctx))
            }
            "indexOf" -> {
                requireArgCount("String.indexOf()", argNodes, 1)
                return target.indexOf(stringArg("String.indexOf()", argNodes, 0, ctx)).toDouble()
            }
            "lastIndexOf" -> {
                requireArgCount("String.lastIndexOf()", argNodes, 1)
                return target.lastIndexOf(stringArg("String.lastIndexOf()", argNodes, 0, ctx)).toDouble()
            }
            "replace" -> {
                requireArgCount("String.replace()", argNodes, 2)
                return target.replace(
                    stringArg("String.replace()", argNodes, 0, ctx),
                    stringArg("String.replace()", argNodes, 1, ctx),
                )
            }
            "trim" -> {
                requireArgCount("String.trim()", argNodes, 0)
                return target.trim()
            }
            "lowercase" -> {
                requireArgCount("String.lowercase()", argNodes, 0)
                return target.lowercase()
            }
            "uppercase" -> {
                requireArgCount("String.uppercase()", argNodes, 0)
                return target.uppercase()
            }
            "substring" -> return substringHelper(target, argNodes, ctx)
            "split" -> return splitHelper(target, argNodes, ctx)
            "mcColor" -> {
                requireArgCount("String.mcColor()", argNodes, 0)
                return translateMinecraftColor(target)
            }
            "stripMcColor" -> {
                requireArgCount("String.stripMcColor()", argNodes, 0)
                return stripMinecraftColor(target)
            }
            "escapeMcColor" -> {
                requireArgCount("String.escapeMcColor()", argNodes, 0)
                return escapeMinecraftColor(target)
            }
            else -> throw DslRuntimeError("String 不支持的方法: $method")
        }
    }

    private fun substringHelper(target: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? {
        if (argNodes.size != 1 && argNodes.size != 2) {
            throw DslRuntimeError("String.substring() 需要 1 或 2 个参数，实际 ${argNodes.size}")
        }
        val start = intArg("String.substring()", argNodes, 0, ctx)
        val end = if (argNodes.size == 2) intArg("String.substring()", argNodes, 1, ctx) else target.length
        if (start < 0 || end < start || end > target.length) {
            throw DslRuntimeError(
                "String.substring() 索引越界: start=$start, end=$end, length=${target.length}",
            )
        }
        return target.substring(start, end)
    }

    private fun splitHelper(target: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? {
        requireArgCount("String.split()", argNodes, 1)
        val separator = stringArg("String.split()", argNodes, 0, ctx)
        if (separator.isEmpty()) {
            throw DslRuntimeError("String.split() 分隔符不能为空")
        }
        // 与原 Java target.split(Pattern.quote(separator), -1) 等价：按字面量分隔符切分，
        // limit = 0（Kotlin 默认）保留末尾空串，对齐 Java split 的 -1 语义。
        return ArrayList(target.split(separator))
    }

    private fun requireArgCount(name: String, argNodes: List<ExprNode>, expected: Int) {
        if (argNodes.size != expected) {
            throw DslRuntimeError("$name 需要 $expected 个参数，实际 ${argNodes.size}")
        }
    }

    private fun stringArg(name: String, argNodes: List<ExprNode>, index: Int, ctx: EvalContext): String {
        val value = ExprEvaluator.eval(argNodes[index], ctx)
        if (value !is String) {
            throw DslRuntimeError("$name 参数必须为 String，实际类型: ${DslValueUtils.classOf(value)}")
        }
        return value
    }

    private fun intArg(name: String, argNodes: List<ExprNode>, index: Int, ctx: EvalContext): Int {
        val value = ExprEvaluator.eval(argNodes[index], ctx)
        if (value !is Number) {
            throw DslRuntimeError("$name 参数必须为 Number，实际类型: ${DslValueUtils.classOf(value)}")
        }
        val number = value.toDouble()
        if (number.isNaN() || number.isInfinite() || number != floor(number)) {
            throw DslRuntimeError("$name 参数必须为整数，实际: $number")
        }
        return number.toLong().toInt()
    }

    private fun translateMinecraftColor(text: String): String {
        val out = StringBuilder(text.length)
        var i = 0
        while (i < text.length) {
            val c = text[i]
            if (c == '&' && i + 1 < text.length && isMinecraftColorCode(text[i + 1])) {
                out.append('§').append((text[++i]).lowercaseChar())
            } else {
                out.append(c)
            }
            i++
        }
        return out.toString()
    }

    @Suppress("ComplexCondition")
    private fun stripMinecraftColor(text: String): String {
        val out = StringBuilder(text.length)
        var i = 0
        while (i < text.length) {
            val c = text[i]
            if ((c == '§' || c == '&') && i + 1 < text.length && isMinecraftColorCode(text[i + 1])) {
                i++
            } else {
                out.append(c)
            }
            i++
        }
        return out.toString()
    }

    private fun escapeMinecraftColor(text: String): String {
        val out = StringBuilder(text.length)
        var i = 0
        while (i < text.length) {
            val c = text[i]
            if (c == '§' && i + 1 < text.length && isMinecraftColorCode(text[i + 1])) {
                out.append('&').append((text[++i]).lowercaseChar())
            } else {
                out.append(c)
            }
            i++
        }
        return out.toString()
    }

    private fun isMinecraftColorCode(c: Char): Boolean {
        val lower = (c).lowercaseChar()
        return (lower in '0'..'9') ||
            (lower in 'a'..'f') ||
            (lower in 'k'..'o') ||
            lower == 'r'
    }

    /**
     * List 上的 helper，按职责分组：
     * - 查询：`size() / isEmpty() / contains(item) / indexOf(item) / firstOrNull() / lastOrNull()`。
     * - 不可变变换（返回新 list，原 list 不变）：
     *   `prepended(item) / appended(item) / added(item) / take(n) / removed(item) / removedAt(index)`。
     *   其中 `added` 是 `appended` 的别名，与 Java/Kotlin 习惯命名对齐。
     * - 可变操作（直接修改原 list）：
     *   `add(item) / remove(item) / removeAt(index)`。
     *   若原 list 不可变（如 [Collections.emptyList] / [java.util.Arrays.asList]），
     *   将抛出 [DslRuntimeError] 提示改用不可变版本。
     */
    @Suppress(
        "UNCHECKED_CAST",
        "CyclomaticComplexMethod",
        "LongMethod",
        "NestedBlockDepth",
        "ReturnCount",
        "ThrowsCount",
    )
    private fun invokeListHelper(target: List<*>, method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? {
        when (method) {
            // ===== 查询 =====
            "size" -> {
                requireArgCount("List.size()", argNodes, 0)
                return target.size.toDouble()
            }
            "isEmpty" -> {
                requireArgCount("List.isEmpty()", argNodes, 0)
                return target.isEmpty()
            }
            "contains" -> {
                requireArgCount("List.contains()", argNodes, 1)
                val item = ExprEvaluator.eval(argNodes[0], ctx)
                return target.contains(item)
            }
            "indexOf" -> {
                requireArgCount("List.indexOf()", argNodes, 1)
                val item = ExprEvaluator.eval(argNodes[0], ctx)
                return target.indexOf(item).toDouble()
            }
            "firstOrNull" -> {
                requireArgCount("List.firstOrNull()", argNodes, 0)
                return if (target.isEmpty()) null else target[0]
            }
            "lastOrNull" -> {
                requireArgCount("List.lastOrNull()", argNodes, 0)
                return if (target.isEmpty()) null else target[target.size - 1]
            }
            // ===== 不可变变换 =====
            "prepended" -> {
                // 不可变头插：返回新 list = [item] + target，原 list 不变
                requireArgCount("List.prepended()", argNodes, 1)
                val item = ExprEvaluator.eval(argNodes[0], ctx)
                val result = ArrayList<Any?>(target.size + 1)
                result.add(item)
                result.addAll(target)
                return result
            }
            "appended", "added" -> {
                // 不可变尾插：返回新 list = target + [item]，原 list 不变；added 为 appended 别名
                requireArgCount("List.$method()", argNodes, 1)
                val item = ExprEvaluator.eval(argNodes[0], ctx)
                val result = ArrayList<Any?>(target.size + 1)
                result.addAll(target)
                result.add(item)
                return result
            }
            "take" -> {
                // 截取前 n 项：用于"保留最近 N 条"的滚动列表场景
                requireArgCount("List.take()", argNodes, 1)
                val v = ExprEvaluator.eval(argNodes[0], ctx)
                if (v !is Number) {
                    throw DslRuntimeError("List.take() 参数必须为 Number，实际类型: ${DslValueUtils.classOf(v)}")
                }
                val n = v.toInt()
                if (n <= 0) return emptyList<Any?>()
                if (n >= target.size) return ArrayList(target)
                return ArrayList<Any?>(target.subList(0, n))
            }
            "removed" -> {
                // 不可变移除：返回新 list，去掉首个 equals item 的元素；不存在则返回原 list 的副本
                requireArgCount("List.removed()", argNodes, 1)
                val item = ExprEvaluator.eval(argNodes[0], ctx)
                val result = ArrayList<Any?>(target.size)
                var removed = false
                for (element in target) {
                    if (!removed && element == item) {
                        removed = true
                        continue
                    }
                    result.add(element)
                }
                return result
            }
            "removedAt" -> {
                // 不可变按下标移除：越界抛错，返回新 list
                requireArgCount("List.removedAt()", argNodes, 1)
                val index = intArg("List.removedAt()", argNodes, 0, ctx)
                if (index < 0 || index >= target.size) {
                    throw DslRuntimeError("List.removedAt() 索引越界: index=$index, size=${target.size}")
                }
                val result = ArrayList<Any?>(target.size - 1)
                for (i in target.indices) {
                    if (i != index) result.add(target[i])
                }
                return result
            }
            // ===== 可变操作 =====
            "add" -> {
                // 可变追加：在原 list 上追加；返回 null（命令式，不暴露 List.add 的 boolean 返回）
                requireArgCount("List.add()", argNodes, 1)
                val item = ExprEvaluator.eval(argNodes[0], ctx)
                try {
                    (target as MutableList<Any?>).add(item)
                } catch (e: UnsupportedOperationException) {
                    throw DslRuntimeError(
                        "List.add() 当前实例不可变，无法修改；请改用 added/appended（返回新 list）",
                        e,
                    )
                }
                return null
            }
            "remove" -> {
                // 可变按值移除：移除原 list 中首个 equals item 的元素；返回是否移除成功
                requireArgCount("List.remove()", argNodes, 1)
                val item = ExprEvaluator.eval(argNodes[0], ctx)
                return try {
                    (target as MutableList<Any?>).remove(item)
                } catch (e: UnsupportedOperationException) {
                    throw DslRuntimeError(
                        "List.remove() 当前实例不可变，无法修改；请改用 removed（返回新 list）",
                        e,
                    )
                }
            }
            "removeAt" -> {
                // 可变按下标移除：越界抛错，返回被移除的元素
                requireArgCount("List.removeAt()", argNodes, 1)
                val index = intArg("List.removeAt()", argNodes, 0, ctx)
                if (index < 0 || index >= target.size) {
                    throw DslRuntimeError("List.removeAt() 索引越界: index=$index, size=${target.size}")
                }
                return try {
                    (target as MutableList<Any?>).removeAt(index)
                } catch (e: UnsupportedOperationException) {
                    throw DslRuntimeError(
                        "List.removeAt() 当前实例不可变，无法修改；请改用 removedAt（返回新 list）",
                        e,
                    )
                }
            }
            else -> throw DslRuntimeError("List 不支持的方法: $method")
        }
    }

    /** ItemView 上的方法分派：NBT 读取 helper。 */
    private fun invokeItemView(view: ItemView, method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? {
        when (method) {
            "nbt", "nbtJson", "nbtList" -> {}
            else -> throw DslRuntimeError("ItemView 不支持的方法: $method")
        }
        if (argNodes.size != 1) {
            throw DslRuntimeError("ItemView.$method() 需要 1 个参数，实际 ${argNodes.size}")
        }
        val pathValue = ExprEvaluator.eval(argNodes[0], ctx)
        val path = if (pathValue == null) null else pathValue.toString()
        return when (method) {
            "nbt" -> view.nbt(path)
            "nbtJson" -> view.nbtJson(path)
            "nbtList" -> view.nbtList(path)
            else -> null
        }
    }

    /** SlotApi 上的方法分派：slot.read(idExpr)，id 在运行期求值。 */
    @Suppress("ThrowsCount")
    private fun invokeSlotApi(api: SlotApi, method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? {
        if ("read" != method) {
            throw DslRuntimeError("SlotApi 不支持的方法: $method")
        }
        if (argNodes.size != 1) {
            throw DslRuntimeError("slot.read 需要 1 个字符串参数，实际 ${argNodes.size}")
        }
        // 求值实参，允许任意返回 String 的表达式（字面量 / state / props / 三元 / 插值 / 函数返回值）
        val idValue = ExprEvaluator.eval(argNodes[0], ctx)
        if (idValue !is String) {
            throw DslRuntimeError("slot.read 的 id 参数求值必须为 String，实际类型: ${DslValueUtils.classOf(idValue)}")
        }
        val view: ItemView? = api.read(idValue)
        return view ?: ItemView.EMPTY
    }

    /** DisplayApi 上的方法分派：display.read(keyExpr, indexExpr) / display.size(keyExpr)，实参在运行期求值。 */
    @Suppress("ThrowsCount", "ReturnCount")
    private fun invokeDisplayApi(api: DisplayApi, method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? {
        if ("size" == method) {
            if (argNodes.size != 1) {
                throw DslRuntimeError("display.size 需要 1 个参数（key），实际 ${argNodes.size}")
            }
            val keyValue = ExprEvaluator.eval(argNodes[0], ctx)
            if (keyValue !is String) {
                throw DslRuntimeError(
                    "display.size 的 key 参数求值必须为 String，实际类型: ${DslValueUtils.classOf(keyValue)}",
                )
            }
            // 与 List.size() 等查询保持一致：数值统一以 Double 进入 DSL 运算 / range
            return api.size(keyValue).toDouble()
        }
        if ("read" != method) {
            throw DslRuntimeError("DisplayApi 不支持的方法: $method")
        }
        if (argNodes.size != 2) {
            throw DslRuntimeError("display.read 需要 2 个参数（key, index），实际 ${argNodes.size}")
        }
        val keyValue = ExprEvaluator.eval(argNodes[0], ctx)
        if (keyValue !is String) {
            throw DslRuntimeError("display.read 的 key 参数求值必须为 String，实际类型: ${DslValueUtils.classOf(keyValue)}")
        }
        val indexValue = ExprEvaluator.eval(argNodes[1], ctx)
        if (indexValue !is Number) {
            throw DslRuntimeError("display.read 的 index 参数求值必须为数值，实际类型: ${DslValueUtils.classOf(indexValue)}")
        }
        return api.read(keyValue, indexValue.toInt())
    }

    /**
     * 特性②:ui("id").method() 元素动作分派。
     *
     * 安全契约:硬编码 switch 白名单,绝不反射;只暴露幂等的交互动作方法 + 少量只读查询。
     * 写动作经 [UiCommandApi.ElementHandle.post]
     * 回渲染线程执行;只读方法同步返回(读单值字段安全)。元素不存在(id 未命中)时写动作 no-op、
     * 只读返回安全默认值。
     */
    @Suppress("CyclomaticComplexMethod", "LongMethod", "ReturnCount", "NestedBlockDepth")
    private fun invokeElementHandle(
        handle: UiCommandApi.ElementHandle,
        method: String,
        argNodes: List<ExprNode>,
        ctx: EvalContext,
    ): Any? {
        val el: UIElement<*>? = handle.element()
        // 求值实参(最多 2 个),保持 DSL 调用约定。
        val args = ArrayList<Any?>(argNodes.size)
        for (a in argNodes) {
            args.add(ExprEvaluator.eval(a, ctx))
        }
        when (method) {
            // ---- 滚动容器:写动作(post 回渲染线程) ----
            "scrollToTop" -> {
                if (el is ScrollableContainer<*>) {
                    handle.post { el.scrollToTop() }
                }
                return null
            }
            "scrollToBottom" -> {
                if (el is ScrollableContainer<*>) {
                    handle.post { el.scrollToBottom() }
                }
                return null
            }
            "scrollTo" -> {
                if (el is ScrollableContainer<*>) {
                    val x = numArg(args, 0)
                    val y = numArg(args, 1)
                    handle.post { el.scrollTo(x, y) }
                }
                return null
            }
            "scrollIntoView" -> {
                if (el is ScrollableContainer<*>) {
                    val childId = if (args.isEmpty()) null else args[0].toString()
                    val alignStr = if (args.size >= 2) args[1].toString() else "nearest"
                    val align = parseViewAlign(alignStr)
                    handle.post { el.scrollIntoView(childId, align) }
                }
                return null
            }
            // ---- 滚动容器:只读查询(同步返回) ----
            "isAtBottom" ->
                return el is ScrollableContainer<*> && el.isAtBottom()
            // ---- VirtualTree:展开/折叠 ----
            "toggleExpand" -> {
                if (el is VirtualTree && args.isNotEmpty()) {
                    val nid = args[0].toString()
                    handle.post { el.toggleExpand(nid) }
                }
                return null
            }
            "expand" -> {
                if (el is VirtualTree && args.isNotEmpty()) {
                    val nid = args[0].toString()
                    handle.post { el.expand(nid) }
                }
                return null
            }
            "collapse" -> {
                if (el is VirtualTree && args.isNotEmpty()) {
                    val nid = args[0].toString()
                    handle.post { el.collapse(nid) }
                }
                return null
            }
            // ---- PageView / Carousel:翻页 ----
            "nextPage" -> {
                if (el is PageView) {
                    handle.post { el.nextPage() }
                }
                return null
            }
            "previousPage" -> {
                if (el is PageView) {
                    handle.post { el.previousPage() }
                }
                return null
            }
            "setCurrentPage" -> {
                if (el is PageView) {
                    val idx = numArg(args, 0).toInt()
                    handle.post { el.setCurrentPage(idx) }
                }
                return null
            }
            "currentPage" ->
                return if (el is PageView) el.currentPage().toDouble() else 0.0
            // ---- Zoomable:缩放 ----
            "zoom" -> {
                if (el is Zoomable) {
                    val zv = numArg(args, 0)
                    handle.post { el.zoom(zv) }
                }
                return null
            }
            "zoomBy" -> {
                if (el is Zoomable) {
                    val steps = numArg(args, 0)
                    handle.post { el.zoomBy(steps) }
                }
                return null
            }
            // ---- 装饰器:刷新/加载收尾 ----
            "finishRefresh" -> {
                if (el is PullToRefresh) {
                    handle.post { el.finishRefresh() }
                }
                return null
            }
            "finishLoad" -> {
                if (el is InfiniteScroll) {
                    val hasMore = args.isNotEmpty() && true == args[0]
                    handle.post { el.finishLoad(hasMore) }
                }
                return null
            }
            // ---- 通用:存在性 ----
            "exists" -> return handle.exists()
            else -> throw DslRuntimeError(
                "ui(id) 不支持的元素方法: $method" +
                    "(仅允许白名单内的交互动作;读取滚动位置请用 scrollY = state.x 双向绑定)",
            )
        }
    }

    /** 取数值实参(缺省 0)。 */
    private fun numArg(args: List<Any?>, i: Int): Float {
        val v = if (i < args.size) args[i] else null
        if (v !is Number) return 0f
        return v.toFloat()
    }

    /** 解析 scrollIntoView 对齐字符串。 */
    private fun parseViewAlign(s: String?): ScrollableContainer.ViewAlign {
        if (s == null) return ScrollableContainer.ViewAlign.NEAREST
        return when (s.trim().lowercase()) {
            "start" -> ScrollableContainer.ViewAlign.START
            "center" -> ScrollableContainer.ViewAlign.CENTER
            "end" -> ScrollableContainer.ViewAlign.END
            else -> ScrollableContainer.ViewAlign.NEAREST
        }
    }

    /** LocalApi 上的方法分派：openScreen / back / close / setTheme / setInteractionMode / warpCursor。 */
    @Suppress("CyclomaticComplexMethod", "LongMethod", "ThrowsCount", "NestedBlockDepth", "ReturnCount")
    private fun invokeLocalApi(api: LocalApi, method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? {
        when (method) {
            "openScreen" -> {
                if (argNodes.size != 1) {
                    throw DslRuntimeError("local.openScreen 需要 1 个参数，实际 ${argNodes.size}")
                }
                val v = ExprEvaluator.eval(argNodes[0], ctx)
                if (v !is String) {
                    throw DslRuntimeError("local.openScreen 参数必须为字符串，实际类型: ${DslValueUtils.classOf(v)}")
                }
                api.openScreen(v)
                return null
            }
            "back" -> {
                if (argNodes.isNotEmpty()) {
                    throw DslRuntimeError("local.back 不接受参数，实际 ${argNodes.size}")
                }
                api.back()
                return null
            }
            "close" -> {
                if (argNodes.isNotEmpty()) {
                    throw DslRuntimeError("local.close 不接受参数，实际 ${argNodes.size}")
                }
                api.close()
                return null
            }
            "command" -> {
                if (argNodes.size != 1) {
                    throw DslRuntimeError("local.command 需要 1 个参数（命令 key），实际 ${argNodes.size}")
                }
                val v = ExprEvaluator.eval(argNodes[0], ctx)
                if (v !is String) {
                    throw DslRuntimeError("local.command 参数必须为字符串，实际类型: ${DslValueUtils.classOf(v)}")
                }
                api.command(v)
                return null
            }
            "playSound" -> {
                if (argNodes.size !in 1..3) {
                    throw DslRuntimeError("local.playSound 需要 1 至 3 个参数，实际 ${argNodes.size}")
                }
                val soundId = ExprEvaluator.eval(argNodes[0], ctx)
                if (soundId !is String) {
                    throw DslRuntimeError(
                        "local.playSound soundId 必须为 String，实际类型: ${DslValueUtils.classOf(soundId)}",
                    )
                }
                val volume = soundNumberArg("volume", argNodes, 1, ctx).coerceAtLeast(0.0)
                val pitch = soundNumberArg("pitch", argNodes, 2, ctx).coerceIn(0.5, 2.0)
                if (!api.playSound(soundId, volume, pitch)) {
                    throw DslRuntimeError("local.playSound 音效资源位置无效: $soundId")
                }
                return null
            }
            "disconnect" -> {
                if (argNodes.isNotEmpty()) {
                    throw DslRuntimeError("local.disconnect 不接受参数，实际 ${argNodes.size}")
                }
                api.disconnect()
                return null
            }
            "quitGame" -> {
                if (argNodes.isNotEmpty()) {
                    throw DslRuntimeError("local.quitGame 不接受参数，实际 ${argNodes.size}")
                }
                api.quitGame()
                return null
            }
            "themes" -> {
                requireArgCount("local.themes", argNodes, 0)
                return ctx.themeEnvironment.getDescriptorProjection()
            }
            "setTheme" -> {
                if (argNodes.size != 1) {
                    throw DslRuntimeError("local.setTheme 需要 1 个参数，实际 ${argNodes.size}")
                }
                val v = ExprEvaluator.eval(argNodes[0], ctx)
                if (v !is String) {
                    throw DslRuntimeError("local.setTheme 参数必须为字符串，实际类型: ${DslValueUtils.classOf(v)}")
                }
                val scheme: String = v
                val mgr: ThemeManager = ctx.themeManager
                if (mgr.isEmpty()) {
                    throw DslRuntimeError(
                        "主题切换不可用：当前 UI 未声明任何备用主题（请检查 ui-themes/ 目录或 screen.theme.scheme）",
                    )
                }
                if (!mgr.contains(scheme)) {
                    throw DslRuntimeError(
                        "未知 theme: $scheme（可用：${mgr.availableNames().joinToString(",")}）",
                    )
                }
                val rr: RendererRegistry = ctx.rendererRegistry
                    ?: throw DslRuntimeError(
                        "local.setTheme 仅在客户端渲染上下文可用（当前 RendererRegistry 未注入）",
                    )
                val targetTheme = mgr.resolve(scheme)
                val switchTheme = UiTask {
                    // 先确保既有控件过渡可启动，再发布普通 ThemeRef 的即时 lookup，避免错误时只切换一侧。
                    rr.beginThemeTransition(
                        targetTheme,
                        ThemeTransition.DEFAULT_DURATION_MS,
                        Easing.EASE_IN_OUT_CUBIC,
                    )
                    check(ctx.themeEnvironment.activate(scheme)) { "已校验的 theme 在激活时不可用: $scheme" }
                }
                ctx.renderThreadExecutor?.execute(switchTheme) ?: switchTheme.run()
                return null
            }
            "setInteractionMode" -> {
                requireArgCount("local.setInteractionMode", argNodes, 1)
                val v = ExprEvaluator.eval(argNodes[0], ctx)
                if (v !is Boolean) {
                    throw DslRuntimeError(
                        "local.setInteractionMode 参数必须为 Boolean，实际类型: ${DslValueUtils.classOf(v)}",
                    )
                }
                api.setInteractionMode(v)
                return null
            }
            "warpCursor" -> {
                requireArgCount("local.warpCursor", argNodes, 2)
                val x = CallArgs.toDouble("local.warpCursor", ExprEvaluator.eval(argNodes[0], ctx))
                val y = CallArgs.toDouble("local.warpCursor", ExprEvaluator.eval(argNodes[1], ctx))
                api.warpCursor(x, y)
                return null
            }
            else -> throw DslRuntimeError("LocalApi 不支持的方法: $method")
        }
    }

    /** 读取可选声音数值参数；缺省为 1，并拒绝非 Number 与非有限值。 */
    private fun soundNumberArg(name: String, args: List<ExprNode>, index: Int, ctx: EvalContext): Double {
        if (index >= args.size) return 1.0
        val value = ExprEvaluator.eval(args[index], ctx)
        if (value !is Number) {
            throw DslRuntimeError(
                "local.playSound $name 必须为 Number，实际类型: ${DslValueUtils.classOf(value)}",
            )
        }
        val number = value.toDouble()
        if (!number.isFinite()) {
            throw DslRuntimeError("local.playSound $name 必须为有限数，实际值: $number")
        }
        return number
    }

    /**
     * v4 §E.3：WorldApi 上的方法分派——`client.world.tiles(radius)` /
     * `client.world.entities(radius)`。
     *
     * 两者均接受 1 个整数 radius 参数，返回 DSL 可直接 `for` 遍历的 List。
     * posX / yaw / dimension 等属性访问不在此处（走 ExprEvaluator 的 MemberAccess 分支）。
     */
    private fun invokeWorldApi(api: WorldApi, method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? {
        when (method) {
            "tiles" -> {
                requireArgCount("client.world.tiles()", argNodes, 1)
                val radius = intArg("client.world.tiles()", argNodes, 0, ctx)
                return api.tiles(radius)
            }
            "entities" -> {
                requireArgCount("client.world.entities()", argNodes, 1)
                val radius = intArg("client.world.entities()", argNodes, 0, ctx)
                return api.entities(radius)
            }
            else -> throw DslRuntimeError("client.world 不支持的方法: $method")
        }
    }

    /** VanillaApi 上的方法分派：支持 container / gui(兼容) / widgets / icons / path。 */
    @Suppress("ReturnCount", "ThrowsCount")
    private fun invokeVanillaApi(api: VanillaApi, method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? {
        if ("container" == method || "gui" == method) {
            if (argNodes.size != 1) {
                throw DslRuntimeError("vanilla.$method 需要 1 个参数，实际 ${argNodes.size}")
            }
            val v = ExprEvaluator.eval(argNodes[0], ctx)
            if (v !is String) {
                throw DslRuntimeError("vanilla.$method 参数必须为字符串，实际类型: ${DslValueUtils.classOf(v)}")
            }
            return api.container(v)
        }
        if ("widgets" == method) {
            requireArgCount("vanilla.widgets()", argNodes, 0)
            return api.widgets()
        }
        if ("icons" == method) {
            requireArgCount("vanilla.icons()", argNodes, 0)
            return api.icons()
        }
        if ("path" == method) {
            if (argNodes.size != 1) {
                throw DslRuntimeError("vanilla.path 需要 1 个参数，实际 ${argNodes.size}")
            }
            val v = ExprEvaluator.eval(argNodes[0], ctx)
            if (v !is String) {
                throw DslRuntimeError("vanilla.path 参数必须为字符串，实际类型: ${DslValueUtils.classOf(v)}")
            }
            return api.path(v)
        }
        throw DslRuntimeError("VanillaApi 不支持的方法: $method")
    }

    /** Phase 2：OptionsApi 方法分派。 */
    @Suppress("CyclomaticComplexMethod", "LongMethod", "ReturnCount", "ThrowsCount")
    private fun invokeOptionsApi(api: OptionsApi, method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? {
        when (method) {
            "set" -> {
                requireArgCount("client.options.set()", argNodes, 2)
                val key = ExprEvaluator.eval(argNodes[0], ctx)
                if (key !is String) {
                    throw DslRuntimeError("client.options.set() 第一个参数必须为字符串，实际类型: ${DslValueUtils.classOf(key)}")
                }
                val value = ExprEvaluator.eval(argNodes[1], ctx)
                api.set(key, value)
                return null
            }
            "get" -> {
                requireArgCount("client.options.get()", argNodes, 1)
                val key = ExprEvaluator.eval(argNodes[0], ctx)
                if (key !is String) {
                    throw DslRuntimeError("client.options.get() 参数必须为字符串，实际类型: ${DslValueUtils.classOf(key)}")
                }
                return api.get(key)
            }
            "save" -> {
                requireArgCount("client.options.save()", argNodes, 0)
                api.save()
                return null
            }
            "availableLanguages" -> {
                requireArgCount("client.options.availableLanguages()", argNodes, 0)
                return api.availableLanguages()
            }
            "availableLanguagesForUi" -> {
                requireArgCount("client.options.availableLanguagesForUi()", argNodes, 0)
                return api.availableLanguagesForUi()
            }
            "availableSoundDevices" -> {
                requireArgCount("client.options.availableSoundDevices()", argNodes, 0)
                return api.availableSoundDevices()
            }
            "reloadResourcePacks" -> {
                requireArgCount("client.options.reloadResourcePacks()", argNodes, 0)
                api.reloadResourcePacks()
                return null
            }
            "playUiSound" -> {
                val soundId = optionalStringArg("client.options.playUiSound()", argNodes, ctx)
                if (soundId == null) api.playUiSound() else api.playUiSound(soundId)
                return null
            }
            "playUiHoverSound" -> {
                val soundId = optionalStringArg("client.options.playUiHoverSound()", argNodes, ctx)
                if (soundId == null) api.playUiHoverSound() else api.playUiHoverSound(soundId)
                return null
            }
            else -> throw DslRuntimeError("client.options 不支持的方法: $method")
        }
    }

    /** 读取可选字符串参数；缺省返回 null，多于 1 个参数报错。 */
    private fun optionalStringArg(name: String, argNodes: List<ExprNode>, ctx: EvalContext): String? {
        if (argNodes.size > 1) {
            throw DslRuntimeError("$name 需要 0 或 1 个参数，实际 ${argNodes.size}")
        }
        return if (argNodes.isEmpty()) null else stringArg(name, argNodes, 0, ctx)
    }

    /** Phase 2：InputApi 方法分派。 */
    @Suppress("CyclomaticComplexMethod", "LongMethod", "ThrowsCount", "ReturnCount")
    private fun invokeInputApi(api: InputApi, method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? {
        when (method) {
            "allBindings" -> {
                requireArgCount("client.input.allBindings()", argNodes, 0)
                return api.allBindings()
            }
            "getKey" -> {
                requireArgCount("client.input.getKey()", argNodes, 1)
                val id = ExprEvaluator.eval(argNodes[0], ctx)
                if (id !is String) {
                    throw DslRuntimeError("client.input.getKey() 参数必须为字符串，实际类型: ${DslValueUtils.classOf(id)}")
                }
                return api.getKey(id)
            }
            "setKey" -> {
                requireArgCount("client.input.setKey()", argNodes, 2)
                val id = ExprEvaluator.eval(argNodes[0], ctx)
                val keyName = ExprEvaluator.eval(argNodes[1], ctx)
                if (id !is String || keyName !is String) {
                    throw DslRuntimeError("client.input.setKey() 参数必须为字符串")
                }
                api.setKey(id, keyName)
                return null
            }
            "conflicts" -> {
                requireArgCount("client.input.conflicts()", argNodes, 1)
                val id = ExprEvaluator.eval(argNodes[0], ctx)
                if (id !is String) {
                    throw DslRuntimeError("client.input.conflicts() 参数必须为字符串，实际类型: ${DslValueUtils.classOf(id)}")
                }
                return api.conflicts(id)
            }
            "getLabel" -> {
                requireArgCount("client.input.getLabel()", argNodes, 1)
                val id = ExprEvaluator.eval(argNodes[0], ctx)
                if (id !is String) {
                    throw DslRuntimeError("client.input.getLabel() 参数必须为字符串，实际类型: ${DslValueUtils.classOf(id)}")
                }
                return api.getLabel(id)
            }
            "getCategory" -> {
                requireArgCount("client.input.getCategory()", argNodes, 1)
                val id = ExprEvaluator.eval(argNodes[0], ctx)
                if (id !is String) {
                    throw DslRuntimeError("client.input.getCategory() 参数必须为字符串，实际类型: ${DslValueUtils.classOf(id)}")
                }
                return api.getCategory(id)
            }
            "isUnbound" -> {
                requireArgCount("client.input.isUnbound()", argNodes, 1)
                val id = ExprEvaluator.eval(argNodes[0], ctx)
                if (id !is String) {
                    throw DslRuntimeError("client.input.isUnbound() 参数必须为字符串，实际类型: ${DslValueUtils.classOf(id)}")
                }
                return api.isUnbound(id)
            }
            "resetAllKeys" -> {
                requireArgCount("client.input.resetAllKeys()", argNodes, 0)
                api.resetAllKeys()
                return null
            }
            "save" -> {
                requireArgCount("client.input.save()", argNodes, 0)
                api.save()
                return null
            }
            else -> throw DslRuntimeError("client.input 不支持的方法: $method")
        }
    }

    /**
     * Map 上的 helper，按职责分组：
     * - typed payload helper：`number(k) / string(k) / bool(k) / map(k) / list(k) / array(k)`，
     *   按期望类型读取并强校验，1 个 String key。
     * - 查询：`size() / isEmpty() / containsKey(k) / has(k) / keys()`。
     *   `has` 是 `containsKey` 的别名。
     * - 不可变变换（返回新 LinkedHashMap，原 map 不变）：
     *   `with(k, v) / without(k)`。
     * - 可变操作（直接修改原 map）：
     *   `put(k, v) / remove(k)`。
     *   若原 map 不可变（如 [Collections.emptyMap]），将抛出 [DslRuntimeError]
     *   提示改用不可变版本。
     */
    @Suppress(
        "UNCHECKED_CAST",
        "CyclomaticComplexMethod",
        "LongMethod",
        "NestedBlockDepth",
        "ReturnCount",
        "ThrowsCount",
    )
    private fun invokeMapHelper(target: Map<*, *>, method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? {
        when (method) {
            // ===== typed payload helper：按期望类型读取并强校验 =====
            "number", "string", "bool", "map", "list", "array" ->
                return invokeMapTypedHelper(target, method, argNodes, ctx)
            // ===== 查询 =====
            "size" -> {
                requireArgCount("Map.size()", argNodes, 0)
                return target.size.toDouble()
            }
            "isEmpty" -> {
                requireArgCount("Map.isEmpty()", argNodes, 0)
                return target.isEmpty()
            }
            "containsKey", "has" -> {
                requireArgCount("Map.$method()", argNodes, 1)
                val key = stringArg("Map.$method()", argNodes, 0, ctx)
                return target.containsKey(key)
            }
            "keys" -> {
                // keys() 强校验：所有 key 必须是 String，否则抛错（与 typed helper 风格一致）
                requireArgCount("Map.keys()", argNodes, 0)
                val result = ArrayList<String>(target.size)
                for (k in target.keys) {
                    if (k !is String) {
                        throw DslRuntimeError("Map.keys() 期望所有 key 为 String，实际遇到: ${DslValueUtils.classOf(k)}")
                    }
                    result.add(k)
                }
                return result
            }
            // ===== 不可变变换 =====
            "with" -> {
                // 不可变写入：返回新 LinkedHashMap = {...原 map, k:v}
                requireArgCount("Map.with()", argNodes, 2)
                val key = stringArg("Map.with()", argNodes, 0, ctx)
                val value = ExprEvaluator.eval(argNodes[1], ctx)
                val result = copyAsStringKeyedMap("Map.with()", target)
                result[key] = value
                return result
            }
            "without" -> {
                // 不可变移除：返回新 LinkedHashMap，去掉指定 key
                requireArgCount("Map.without()", argNodes, 1)
                val key = stringArg("Map.without()", argNodes, 0, ctx)
                val result = copyAsStringKeyedMap("Map.without()", target)
                result.remove(key)
                return result
            }
            // ===== 可变操作 =====
            "put" -> {
                // 可变写入：直接修改原 map；返回 null（命令式）
                requireArgCount("Map.put()", argNodes, 2)
                val key = stringArg("Map.put()", argNodes, 0, ctx)
                val value = ExprEvaluator.eval(argNodes[1], ctx)
                try {
                    (target as MutableMap<String, Any?>)[key] = value
                } catch (e: UnsupportedOperationException) {
                    throw DslRuntimeError("Map.put() 当前实例不可变，无法修改；请改用 with（返回新 map）", e)
                }
                return null
            }
            "remove" -> {
                // 可变移除：从原 map 移除指定 key；返回被移除的 value（不存在返回 null）
                requireArgCount("Map.remove()", argNodes, 1)
                val key = stringArg("Map.remove()", argNodes, 0, ctx)
                return try {
                    (target as MutableMap<String, Any?>).remove(key)
                } catch (e: UnsupportedOperationException) {
                    throw DslRuntimeError("Map.remove() 当前实例不可变，无法修改；请改用 without（返回新 map）", e)
                }
            }
            else -> throw DslRuntimeError("Map 不支持的方法: $method")
        }
    }

    /**
     * 把任意 Map 拷贝成 `LinkedHashMap<String, Object>`，遇到非 String key 抛错。
     * 与 `keys()` 强校验风格一致，确保不可变变换不会静默丢失/转换非 String key。
     */
    private fun copyAsStringKeyedMap(hint: String, source: Map<*, *>): LinkedHashMap<String, Any?> {
        val result = LinkedHashMap<String, Any?>(source.size)
        for (e in source.entries) {
            val k = e.key
            if (k !is String) {
                throw DslRuntimeError("$hint 期望所有 key 为 String，实际遇到: ${DslValueUtils.classOf(k)}")
            }
            result[k] = e.value
        }
        return result
    }

    /**
     * Map typed payload helper：按 method 把指定 key 的值强转为期望类型。
     *
     * 统一约定：1 个 String key；value 为 null 时返回 null（不抛错）；
     * 类型不匹配直接抛 [DslRuntimeError]。
     */
    @Suppress("CyclomaticComplexMethod", "ReturnCount", "NestedBlockDepth", "ThrowsCount")
    private fun invokeMapTypedHelper(
        target: Map<*, *>,
        method: String,
        argNodes: List<ExprNode>,
        ctx: EvalContext,
    ): Any? {
        if (argNodes.size != 1) {
            throw DslRuntimeError("payload.$method() 需要 1 个参数，实际 ${argNodes.size}")
        }
        val keyValue = ExprEvaluator.eval(argNodes[0], ctx)
        if (keyValue !is String) {
            throw DslRuntimeError("payload.$method() 参数必须为字符串，实际类型: ${DslValueUtils.classOf(keyValue)}")
        }
        val value = target[keyValue]
        if (value == null) return null
        when (method) {
            "number" -> if (value is Number) return value.toDouble()
            "string" -> if (value is String) return value
            "bool" -> if (value is Boolean) return value
            "map" -> if (value is Map<*, *>) return LinkedHashMap(value)
            "list" -> if (value is List<*>) return ArrayList(value)
            "array" -> {
                // M6-f §8.5：array(key) 返回 List<Map<String, Object>>，
                // 每个元素必须是 Map，否则抛 DslRuntimeError。
                if (value is List<*>) {
                    val src = value
                    val out = ArrayList<Map<String, Any?>>(src.size)
                    for (item in src) {
                        if (item !is Map<*, *>) {
                            throw DslRuntimeError(
                                "payload.array(\"$keyValue\") 元素类型不匹配，期望 Map，实际: ${DslValueUtils.classOf(item)}",
                            )
                        }
                        @Suppress("UNCHECKED_CAST")
                        val m = item as Map<String, Any?>
                        out.add(LinkedHashMap(m))
                    }
                    return out
                }
            }
            else -> {}
        }
        throw DslRuntimeError("payload.$method(\"$keyValue\") 类型不匹配，实际: ${DslValueUtils.classOf(value)}")
    }
}
